import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, MessageCircle } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";

const FAQ_DATA = [
    {
        cat: "Démarrage",
        items: [
            {
                q: "Comment Ticketify récupère-t-il mes tickets ?",
                a: "Trois voies : (1) e-reçus directement de votre boîte Gmail, (2) e-tickets fidélité après paiement physique avec carte (Carrefour, Monoprix, Decathlon…), (3) photo manuelle pour les commerces qui n'impriment que sur papier (boulanger, bistrot…). L'app guide à chaque étape.",
            },
            {
                q: "Combien de temps pour configurer ?",
                a: "5 minutes : créer le compte, connecter Gmail (1 clic OAuth), importer un relevé bancaire CSV. À partir de là, c'est automatique.",
            },
            {
                q: "Y a-t-il un onboarding ?",
                a: "Oui, un assistant pas-à-pas vous aide à connecter vos comptes après l'inscription. Vous pouvez aussi tester d'abord avec des données démo en 1 clic.",
            },
        ],
    },
    {
        cat: "Marchands & couverture",
        items: [
            {
                q: "Quels marchands sont reconnus automatiquement ?",
                a: "Plus de 200 marchands : Amazon, SNCF, Uber, Apple, Google, Microsoft, OVH, Stripe, Booking, Airbnb, Air France, Total Energies, Sunday, Deliveroo, Carrefour Drive… La liste complète est sur la page Comment ça marche, et s'étoffe chaque semaine.",
            },
            {
                q: "Et Carrefour/Monoprix en magasin physique ?",
                a: "Présentez votre carte fidélité au TPE et activez l'opt-in « e-ticket par mail » dans l'app du marchand. Le ticket arrive ensuite par mail à chaque passage en caisse.",
            },
            {
                q: "Et la boulangerie ou le bistrot du coin ?",
                a: "L'app affiche la ligne de la transaction bancaire avec un badge orange « Photo ». Un tap, une photo, et GPT-5.2 vision extrait articles + TVA + SIRET en 5 secondes.",
            },
        ],
    },
    {
        cat: "Sécurité & données",
        items: [
            {
                q: "Mes données bancaires sont-elles en sécurité ?",
                a: "Hébergement France, chiffrement AES-256 au repos, TLS 1.3 en transit. Aucun employé Ticketify n'a accès à vos données sans procédure approuvée et journalisée.",
            },
            {
                q: "Que se passe-t-il si je supprime mon compte ?",
                a: "Vos données sont anonymisées sous 30 jours, sauf les justificatifs comptables qui doivent être conservés 10 ans par obligation légale (art. L102 B LPF). Vous pouvez exporter toutes vos données au format JSON avant suppression.",
            },
            {
                q: "Ticketify revend-il mes données ?",
                a: "Jamais. Notre modèle économique = abonnement. Pas de pub, pas de revente, pas de profilage tiers.",
            },
        ],
    },
    {
        cat: "Tarifs & facturation",
        items: [
            {
                q: "Y a-t-il un essai gratuit ?",
                a: "Oui. Le plan Découverte est gratuit : relevés bancaires et analyse illimités, jusqu'à 100 transactions justifiées, connexion Gmail et recherche automatique des justificatifs — sans carte demandée. Vous pouvez passer en Pro à tout moment pour aller plus loin.",
            },
            {
                q: "Puis-je changer de plan ?",
                a: "À tout moment depuis vos paramètres. L'ajustement de facturation est prorata.",
            },
            {
                q: "Y a-t-il un engagement ?",
                a: "Aucun. Tous les plans sont mensuels, résiliables en 1 clic.",
            },
            {
                q: "Comment récupérer mes factures Ticketify ?",
                a: "Dans Paramètres > Factures et plans. Chaque transaction est téléchargeable en PDF conforme art. 289 CGI.",
            },
        ],
    },
    {
        cat: "Compta & comptables",
        items: [
            {
                q: "Mes justificatifs sont-ils acceptés en compta ?",
                a: "Oui. Le PDF généré contient marchand, date, montant, articles, TVA, SIRET et n° de facture quand disponibles — conformes aux exigences URSSAF et de l'administration fiscale.",
            },
            {
                q: "Puis-je donner accès à mon comptable ?",
                a: "Sur le plan Business, vous pouvez inviter votre comptable en lecture. L'export FEC et les intégrations Pennylane/QuickBooks/Sage arrivent prochainement.",
            },
            {
                q: "Combien de temps les justificatifs sont-ils conservés ?",
                a: "10 ans, conformément à l'article L102 B du Livre des procédures fiscales.",
            },
        ],
    },
];


export default function FAQ() {
    return (
        <div className="min-h-screen">
            <PublicHeader />

            <section className="pt-32 pb-12 sm:pt-44 sm:pb-16 px-6 sm:px-8 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">FAQ</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1D1D1F] leading-[1.05]">
                    Vos questions,<br />
                    <span className="bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                        nos réponses honnêtes.
                    </span>
                </h1>
            </section>

            <section className="px-6 sm:px-8 pb-24">
                <div className="max-w-3xl mx-auto space-y-12">
                    {FAQ_DATA.map((cat) => (
                        <div key={cat.cat}>
                            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-4">
                                {cat.cat}
                            </h2>
                            <div className="tk-card divide-y divide-black/5 overflow-hidden">
                                {cat.items.map((q) => (
                                    <FAQItem key={q.q} q={q.q} a={q.a} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="px-6 sm:px-8 pb-24">
                <div className="max-w-2xl mx-auto tk-card p-8 text-center">
                    <MessageCircle className="w-8 h-8 text-[#007AFF] mx-auto mb-3" />
                    <h2 className="text-xl font-semibold text-[#1D1D1F]">
                        Pas trouvé votre réponse ?
                    </h2>
                    <p className="mt-2 text-sm text-[#86868B]">
                        L&apos;équipe répond dans les plus brefs délais.
                    </p>
                    <Link to="/register" className="tk-btn tk-btn-secondary mt-5 inline-flex">
                        Créer un compte et écrire au support
                    </Link>
                </div>
            </section>

            <footer className="border-t border-black/5 py-10 px-6 sm:px-8 text-center text-sm text-[#86868B]">
                © 2026 Ticketify
            </footer>
        </div>
    );
}

function FAQItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <button
            onClick={() => setOpen((v) => !v)}
            className="w-full text-left p-5 hover:bg-black/[0.02] transition"
            data-testid={`faq-${q.slice(0, 18)}`}
        >
            <div className="flex items-start justify-between gap-4">
                <span className="font-medium text-[#1D1D1F] text-[15px]">{q}</span>
                <ChevronDown
                    className={`w-5 h-5 text-[#86868B] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </div>
            {open && (
                <p className="mt-3 text-sm text-[#86868B] leading-relaxed">{a}</p>
            )}
        </button>
    );
}
